import { useState, useCallback } from 'react';
import { axiosInstance } from '@/api/client';
import type { ImageKind, ImageUploadOut } from '@/api/generated/schemas';
import type { ImageUploadInContentType } from '@/api/generated/schemas';

interface UploadResult {
  imageKey: string;
  publicUrl: string;
}

interface UseSignedUploadReturn {
  upload: (file: File, imageKind: ImageKind) => Promise<UploadResult>;
  isUploading: boolean;
  error: string | null;
  reset: () => void;
}

export function useSignedUpload(): UseSignedUploadReturn {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setError(null);
    setIsUploading(false);
  }, []);

  const upload = useCallback(
    async (file: File, imageKind: ImageKind): Promise<UploadResult> => {
      setIsUploading(true);
      setError(null);
      try {
        const contentType = file.type as ImageUploadInContentType;

        const { data: uploadOut } = await axiosInstance.post<ImageUploadOut>(
          `/user_catalog/images/${imageKind}/upload`,
          { content_type: contentType }
        );

        const response = await fetch(uploadOut.upload_url, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': contentType },
        });

        if (!response.ok) {
          throw new Error(`S3 upload failed: ${response.status}`);
        }

        return {
          imageKey: uploadOut.image_key,
          publicUrl: uploadOut.public_url,
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        setError(message);
        throw err;
      } finally {
        setIsUploading(false);
      }
    },
    []
  );

  return { upload, isUploading, error, reset };
}
